import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Gift, Save, Loader2, Mail, Send, Calendar, User } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BirthdaySettings = ({ token }) => {
  const [settings, setSettings] = useState({
    enabled: false,
    days_before: 2,
    email_supplier_female: '',
    email_supplier_male: '',
    email_supplier_fallback: '',
    email_subject: 'Geburtstag - Kundengeschenk vorbereiten',
    email_message: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [upcoming, setUpcoming] = useState([]);
  const [loadingUpcoming, setLoadingUpcoming] = useState(false);

  useEffect(() => {
    loadSettings();
    loadUpcoming();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await axios.get(`${API}/birthday-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(prev => ({ ...prev, ...res.data }));
    } catch (err) {
      console.error('Fehler beim Laden:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadUpcoming = async () => {
    setLoadingUpcoming(true);
    try {
      const res = await axios.get(`${API}/birthday-upcoming?days=14`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUpcoming(res.data.upcoming || []);
    } catch (err) {
      console.error('Fehler beim Laden:', err);
    } finally {
      setLoadingUpcoming(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/birthday-settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Geburtstags-Einstellungen gespeichert');
    } catch (err) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleTestSend = async () => {
    setTesting(true);
    try {
      const res = await axios.post(`${API}/birthday-check`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = res.data;
      if (data.sent > 0) {
        toast.success(`${data.sent} E-Mail(s) gesendet`);
      } else {
        toast.info(data.message || 'Keine Geburtstage gefunden');
      }
      if (data.errors?.length > 0) {
        toast.error(`Fehler: ${data.errors.join(', ')}`);
      }
    } catch (err) {
      toast.error('Fehler beim Test-Versand');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="birthday-settings">
      {/* Haupteinstellungen */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5 text-pink-500" />
            Geburtstags-E-Mails
          </CardTitle>
          <CardDescription>
            Automatisch Lieferanten per E-Mail benachrichtigen, damit ein Geschenk vorbereitet wird
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Aktivierung */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border" data-testid="birthday-enabled-toggle">
            <div>
              <p className="font-medium">Automatische Geburtstags-E-Mails</p>
              <p className="text-sm text-gray-500">E-Mails an Lieferanten vor Kundengeburtstagen senden</p>
            </div>
            <Switch
              checked={settings.enabled}
              onCheckedChange={(val) => setSettings(prev => ({ ...prev, enabled: val }))}
            />
          </div>

          {/* Tage vorher */}
          <div>
            <Label>Tage im Voraus</Label>
            <div className="flex items-center gap-3 mt-1">
              <Input
                type="number"
                min={1}
                max={14}
                value={settings.days_before}
                onChange={(e) => setSettings(prev => ({ ...prev, days_before: parseInt(e.target.value) || 2 }))}
                className="w-24"
                data-testid="birthday-days-before"
              />
              <span className="text-sm text-gray-500">Tage vor dem Geburtstag</span>
            </div>
          </div>

          {/* Lieferanten E-Mail Adressen */}
          <div className="space-y-4">
            <h4 className="font-medium flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Lieferanten E-Mail-Adressen
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-1">
                  <User className="w-3 h-3" /> Lieferant Frauen
                </Label>
                <Input
                  type="email"
                  placeholder="lieferant-frauen@beispiel.ch"
                  value={settings.email_supplier_female}
                  onChange={(e) => setSettings(prev => ({ ...prev, email_supplier_female: e.target.value }))}
                  className="mt-1"
                  data-testid="birthday-email-female"
                />
                <p className="text-xs text-gray-400 mt-1">Wird verwendet wenn Kunde weiblich ist</p>
              </div>

              <div>
                <Label className="flex items-center gap-1">
                  <User className="w-3 h-3" /> Lieferant Herren
                </Label>
                <Input
                  type="email"
                  placeholder="lieferant-herren@beispiel.ch"
                  value={settings.email_supplier_male}
                  onChange={(e) => setSettings(prev => ({ ...prev, email_supplier_male: e.target.value }))}
                  className="mt-1"
                  data-testid="birthday-email-male"
                />
                <p className="text-xs text-gray-400 mt-1">Wird verwendet wenn Kunde männlich ist</p>
              </div>
            </div>

            <div>
              <Label>Fallback E-Mail (wenn Geschlecht unbekannt)</Label>
              <Input
                type="email"
                placeholder="lieferant@beispiel.ch"
                value={settings.email_supplier_fallback}
                onChange={(e) => setSettings(prev => ({ ...prev, email_supplier_fallback: e.target.value }))}
                className="mt-1"
                data-testid="birthday-email-fallback"
              />
            </div>
          </div>

          {/* E-Mail Betreff */}
          <div>
            <Label>E-Mail Betreff</Label>
            <Input
              value={settings.email_subject}
              onChange={(e) => setSettings(prev => ({ ...prev, email_subject: e.target.value }))}
              className="mt-1"
              data-testid="birthday-email-subject"
            />
          </div>

          {/* Zusätzliche Nachricht */}
          <div>
            <Label>Zusätzliche Nachricht (optional)</Label>
            <textarea
              value={settings.email_message}
              onChange={(e) => setSettings(prev => ({ ...prev, email_message: e.target.value }))}
              className="mt-1 w-full p-3 border rounded-lg text-sm resize-none h-20 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              placeholder="z.B. Bitte Geschenk an die Studioadresse liefern..."
              data-testid="birthday-email-message"
            />
          </div>

          {/* Buttons */}
          <div className="flex items-center gap-3 pt-2">
            <Button onClick={handleSave} disabled={saving} data-testid="birthday-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Speichern
            </Button>
            <Button variant="outline" onClick={handleTestSend} disabled={testing} data-testid="birthday-test-btn">
              {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Jetzt prüfen & senden
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Nächste Geburtstage */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Calendar className="h-5 w-5 text-blue-500" />
            Nächste Geburtstage (14 Tage)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingUpcoming ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            </div>
          ) : upcoming.length === 0 ? (
            <p className="text-sm text-gray-500 py-4 text-center">Keine Geburtstage in den nächsten 14 Tagen</p>
          ) : (
            <div className="space-y-2">
              {upcoming.map((c, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border" data-testid={`birthday-upcoming-${i}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center">
                      <Gift className="w-5 h-5 text-pink-500" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{c.first_name} {c.last_name}</p>
                      <p className="text-xs text-gray-500">{c.birthday} (wird {c.age})</p>
                    </div>
                  </div>
                  <div className="text-right">
                    {c.days_until === 0 ? (
                      <Badge className="bg-pink-500 text-white">Heute!</Badge>
                    ) : c.days_until === 1 ? (
                      <Badge className="bg-orange-500 text-white">Morgen</Badge>
                    ) : (
                      <Badge variant="outline">in {c.days_until} Tagen</Badge>
                    )}
                    {c.gender && (
                      <p className="text-xs text-gray-400 mt-1">
                        {c.gender === 'female' || c.gender === 'weiblich' ? 'Frau' : 
                         c.gender === 'male' || c.gender === 'männlich' ? 'Herr' : c.gender}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BirthdaySettings;
